import { omit } from "lodash-es";
import { Dialog } from "@ark-ui/solid";
import { IconButton } from "../../../components/button/Button";
import GlobalTabs from "../../../components/tabs/GlobalTabs";
import { toField } from "../../../fields/fields";
import SimpleTable from "../../../components/table/SimpleTable";
import FormatDate from "../../../components/formatting/FormatDate";
import type { Component } from "solid-js";
import { createMemo, Show } from "solid-js";
import TextLink from "../../../components/link/TextLink";
import { useTranslation } from "../../../state/i18n";
import type { Submission } from "../../../types/Submission";
import type { FieldConfig, FieldDataNameProperties, FieldLabelTextProperties } from "../../../fields/types";
import { renderFieldValue } from "../../../fields/fieldValueRenderer";
import type { FormVersion } from "../../../state/formVersion";

type ViewSubmissionModalProps = {
  formName: string;
  formVersion: FormVersion;
  submission?: Submission;
  fields: FieldConfig<Required<FieldDataNameProperties & FieldLabelTextProperties>>[];
};
const ViewSubmissionModal: Component<ViewSubmissionModalProps> = (props) => {
  const { t } = useTranslation();

  return (
    <Show when={props.submission}>
      {(submission) => (
        <div class="flex max-h-160 w-146 flex-col gap-6 overflow-y-hidden p-6 shadow-elevation-light-5">
          <div>
            <Dialog.Title>
              <h3 class="text-header-lg text-primary">{props.formName}</h3>
              <h4 class="text-medium-regular text-secondary">
                {t(($) => $.VIEW_SUBMISSION_MODAL_DESCRIPTION, { serialNumber: submission().meta.serialNumber })}
              </h4>
            </Dialog.Title>
            <div class="absolute top-3 right-3">
              <Dialog.CloseTrigger
                asChild={(parentProps) => (
                  <IconButton
                    {...omit(parentProps(), "children")}
                    icon="x"
                    aria-label={t(($) => $.CLOSE_ARIA)}
                    size="large"
                    variant="tertiary"
                  />
                )}
              />
            </div>
          </div>

          <GlobalTabs
            initialTab="fields"
            items={[
              {
                key: "fields",
                label: () => t(($) => $.VIEW_SUBMISSION_MODAL_FIELDS_TAB_TITLE),
                Slot: () => (
                  <SubmissionData submission={submission()} formVersion={props.formVersion} fields={props.fields} />
                ),
              },
              {
                key: "meta",
                label: () => t(($) => $.VIEW_SUBMISSION_MODAL_META_DATA_TAB_TITLE),
                Slot: () => <SubmissionMetaData submission={submission()} />,
              },
            ]}
          />
        </div>
      )}
    </Show>
  );
};

const SubmissionData: Component<{
  submission: Submission;
  formVersion: FormVersion;
  fields: FieldConfig<Required<FieldDataNameProperties & FieldLabelTextProperties>>[];
}> = (props) => {
  const data = createMemo(() => {
    const submissionData = props.submission.data;
    const formVersion = props.formVersion;

    return props.fields.map((fieldConfig) => ({
      label: toField(fieldConfig.type).displayLabel(fieldConfig.properties),
      value: renderFieldValue(submissionData[fieldConfig.properties.data_name], fieldConfig, formVersion),
    }));
  });

  return (
    <div class="mt-4">
      <SimpleTable data={data()} />
    </div>
  );
};

const SubmissionMetaData: Component<{ submission: Submission }> = (props) => {
  const { t } = useTranslation();

  return (
    <div class="mt-4">
      <SimpleTable
        data={[
          {
            label: t(($) => $.VIEW_SUBMISSION_MODAL_META_DATA_SERIAL_NUMBER),
            value: props.submission.meta.serialNumber,
          },
          { label: t(($) => $.VIEW_SUBMISSION_MODAL_META_DATA_ID), value: props.submission.id.toString() },
          { label: t(($) => $.VIEW_SUBMISSION_MODAL_META_DATA_TASK), value: props.submission.meta.taskId?.toString() },
          { label: t(($) => $.VIEW_SUBMISSION_MODAL_META_DATA_SUBMITTED_BY), value: props.submission.info.username },
          {
            label: t(($) => $.VIEW_SUBMISSION_MODAL_META_DATA_SUBMITTED_ON),
            value: <FormatDate value={props.submission.info.date} />,
          },
          {
            label: t(($) => $.VIEW_SUBMISSION_MODAL_META_DATA_LOCATION),
            value: (
              <Show
                when={props.submission.meta.location}
                fallback={t(($) => $.VIEW_SUBMISSION_MODAL_META_DATA_LOCATION_NOT_RECORDED)}
              >
                {(loc) => (
                  <TextLink
                    label={t(($) => $.VIEW_SUBMISSION_MODAL_META_DATA_LOCATION_SHOW_ON_GOOGLE_MAPS)}
                    href={`https://maps.google.com?q=${loc().latitude},${loc().longitude}`}
                    isExternal
                  />
                )}
              </Show>
            ),
          },
          { label: t(($) => $.VIEW_SUBMISSION_MODAL_META_DATA_DEVICE), value: props.submission.meta.device.name },
          {
            label: t(($) => $.VIEW_SUBMISSION_MODAL_META_DATA_APP_VERSION),
            value: props.submission.meta.device.appVersion,
          },
          { label: t(($) => $.VIEW_SUBMISSION_MODAL_META_DATA_SUMMARY), value: props.submission.info.summary },
          { label: t(($) => $.VIEW_SUBMISSION_MODAL_META_DATA_NETWORK), value: props.submission.meta.device.network },
        ]}
      />
    </div>
  );
};

export default ViewSubmissionModal;
